import React, { forwardRef } from 'react'
import { alpha, useTheme } from '@material-ui/core/styles'

// Shared tonal chip for statuses and file types on Home and Files: a 14% tint of
// the palette colour with the darker (light mode) or lighter (dark mode) shade
// as the text colour, so it stays readable on both surfaces.
// tone: 'primary' | 'secondary' | 'somatic' | 'info' | 'success' | 'warning' | 'error' | 'neutral'

// One file-type colour map, used by the Files table and the analysis wizard.
export const FILE_TYPE_TONES = {
  VCF: 'info',
  FASTQ: 'primary',
  BAM: 'secondary',
  BED: 'warning',
  PANEL: 'neutral',
}

export const getFileTypeTone = (type) => {
  const key = String(type || '').toUpperCase()
  if (key.includes('VCF')) return FILE_TYPE_TONES.VCF
  if (key.includes('FASTQ')) return FILE_TYPE_TONES.FASTQ
  if (key.includes('BAM')) return FILE_TYPE_TONES.BAM
  if (key.includes('BED')) return FILE_TYPE_TONES.BED
  return FILE_TYPE_TONES.PANEL
}

export const getToneColors = (theme, tone = 'neutral') => {
  const isLight = theme.palette.mode === 'light'
  const pal = tone !== 'neutral' ? theme.palette[tone] : null
  if (!pal) {
    return {
      main: theme.palette.grey[500],
      text: theme.palette.text.secondary,
      bg: alpha(theme.palette.grey[500], 0.16),
    }
  }
  return {
    main: pal.main,
    text: isLight ? pal.dark : pal.light,
    bg: alpha(pal.main, 0.14),
  }
}

export const TONAL_CHIP_HEIGHT = 22 // same height as src/components/Label

const TonalChip = forwardRef(function TonalChip(
  { tone = 'neutral', label, dot = false, style, children, ...other },
  ref,
) {
  const theme = useTheme()
  const colors = getToneColors(theme, tone)

  return (
    <span
      ref={ref}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        height: TONAL_CHIP_HEIGHT,
        maxWidth: '100%',
        padding: '0 8px',
        borderRadius: theme.shape.borderRadius,
        backgroundColor: colors.bg,
        color: colors.text,
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.pxToRem(13),
        fontWeight: 600,
        lineHeight: 1,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        fontVariantNumeric: 'tabular-nums',
        verticalAlign: 'middle',
        ...style,
      }}
      {...other}
    >
      {dot && (
        <span
          aria-hidden
          style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: colors.main, flexShrink: 0 }}
        />
      )}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{label ?? children}</span>
    </span>
  )
})

export default TonalChip
